import type { Shop } from '@prisma/client'
import { Link } from '@remix-run/react'
import { AlertCircleIcon, AtSignIcon, MailIcon } from 'lucide-react'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'

import { makeShopOwnPath } from '~/helpers/routes'
import { useOptionalSeller } from '~/hooks/data/use-seller'
import { useShop } from '~/hooks/data/use-shop'
import { useOnboardingShake } from '~/hooks/use-onboarding-shake'
import { Button, Tooltip } from '~/ui'
import { cn } from '~/utils/classnames'

import { SupportEmailModal } from './SupportEmailModal'
import { SupportInstagramModal } from './SupportInstagramModal'

const footerLinkStyle = cn(
  'block font-medium text-sm text-foreground hover:underline'
)

export function ShopFooter() {
  const shop = useShop()
  const { t } = useTranslation()
  const seller = useOptionalSeller()

  const [openEmailSupportModal, setOpenEmailSupportModal] = useState(false)
  const [openInstagramSupportModal, setOpenInstagramSupportModal] =
    useState(false)

  const hasLegalPolicies =
    shop.returnPolicy || shop.termsAndConditions || shop.privacyPolicy

  const triggerSupportModal =
    (entityId: 'supportEmail' | 'instagramHandle') => () => {
      const actionsGroup = {
        supportEmail: setOpenEmailSupportModal,
        instagramHandle: setOpenInstagramSupportModal,
      }

      actionsGroup[entityId](true)
    }

  return (
    <>
      <SupportEmailModal
        open={openEmailSupportModal}
        onClose={() => setOpenEmailSupportModal(false)}
      />

      <SupportInstagramModal
        open={openInstagramSupportModal}
        onClose={() => setOpenInstagramSupportModal(false)}
      />

      <footer className="border-border/4 bg-background/80 relative border-t p-4 backdrop-blur-sm @md:px-6">
        <h2 className="text-md text-foreground mb-6 font-medium @lg:hidden">
          {shop.name}
        </h2>

        <div className="grid items-center gap-3 @md:grid-cols-2 @lg:grid-cols-[1fr_auto_1fr]">
          <div className="flex flex-col @md:flex-row @md:items-center @lg:gap-9">
            {(seller || hasLegalPolicies) && (
              <Link
                className={footerLinkStyle}
                to={makeShopOwnPath(shop, '/legal')}
              >
                {t('components.footer.legal')}
              </Link>
            )}
          </div>

          <h2 className="text-md text-foreground hidden font-medium @lg:block">
            {shop.name}
          </h2>

          <ShopCustomerSupport
            triggerSupportModal={triggerSupportModal}
            shop={shop}
          />
        </div>
      </footer>
    </>
  )
}

function ShopCustomerSupport({
  triggerSupportModal,
  shop,
}: {
  triggerSupportModal: (
    entityId: 'supportEmail' | 'instagramHandle'
  ) => () => void
  shop: Shop
}) {
  const { t } = useTranslation()

  const supportContainerRef = useOnboardingShake<HTMLDivElement | null>(
    'support_email'
  )

  const hasSupportEmail = shop.supportEmail && shop.supportEmail?.length > 0
  const hasPendingSupportEmail =
    shop.pendingSupportEmail && shop.pendingSupportEmail?.length > 0
  const hasInstagram = shop.instagramHandle && shop.instagramHandle?.length > 0

  const seller = useOptionalSeller()

  const parseInstagramHandle = (link: string) => {
    const splitted = link?.split('instagram.com/')
    const handle = splitted[splitted.length - 1] || '...'
    return handle.startsWith('@') ? handle.slice(1) : handle
  }

  return (
    <div className="flex flex-col gap-3 @md:flex-row @md:items-center @md:justify-end">
      {seller ? (
        <>
          <div ref={supportContainerRef}>
            <Tooltip delayIn={0} disabled={!hasPendingSupportEmail}>
              <Tooltip.Trigger asChild>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={triggerSupportModal('supportEmail')}
                >
                  <MailIcon />
                  {(hasPendingSupportEmail ?? hasSupportEmail) ? (
                    <>
                      <span>
                        {shop.pendingSupportEmail ?? shop.supportEmail}
                      </span>
                      {hasPendingSupportEmail && (
                        <AlertCircleIcon className="opacity-64" />
                      )}
                    </>
                  ) : (
                    t('components.footer.set_customer_support')
                  )}
                </Button>
              </Tooltip.Trigger>
              <Tooltip.Content className="w-60">
                Please follow the link in your inbox to confirm your e-mail
                address
              </Tooltip.Content>
            </Tooltip>
          </div>
          <div>
            <Button
              size="sm"
              variant="outline"
              onClick={triggerSupportModal('instagramHandle')}
            >
              {hasInstagram ? (
                <span className={cn('max-w-[15ch] truncate')}>
                  {`@${parseInstagramHandle(shop.instagramHandle!)}`}
                </span>
              ) : (
                <>
                  <AtSignIcon />
                  {t('components.footer.connect_instagram')}
                </>
              )}
            </Button>
          </div>
        </>
      ) : (
        <>
          {hasSupportEmail && (
            <a className={footerLinkStyle} href={`mailto:${shop.supportEmail}`}>
              {t('components.footer.customer_support')}
            </a>
          )}
          {hasInstagram && (
            <a
              className={cn(footerLinkStyle, 'max-w-[17ch] truncate')}
              href={`https://www.instagram.com/${parseInstagramHandle(
                shop.instagramHandle!
              )}`}
              target="_blank"
              title={shop.instagramHandle!}
              rel="noreferrer"
            >
              {`@${parseInstagramHandle(shop.instagramHandle!)}`}
            </a>
          )}
        </>
      )}
    </div>
  )
}
